import { useState, useEffect, useMemo } from 'react';
import { X, Search, Building2, User } from 'lucide-react';
import { useClientesCobranzas } from '../../graphql/tesoreria';
import ModalBase from '@/components/Modal/ModalBase.jsx';

/**
 * SelectorClienteProveedor - Componente modal para seleccionar clientes o proveedores
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el modal está abierto
 * @param {Function} props.onClose - Callback al cerrar el modal
 * @param {Function} props.onSelect - Callback al seleccionar una entidad (recibe el objeto seleccionado)
 * @param {'cliente'|'proveedor'} props.mode - Modo de selección
 * @param {string} props.selectedId - ID de la entidad actualmente seleccionada
 */
export default function SelectorClienteProveedor({
  isOpen,
  onClose,
  onSelect,
  mode = 'cliente',
  selectedId = null,
}) {
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  const filtrosClientes = useMemo(() => {
    if (!search) return {};
    return { search };
  }, [search]);

  const dummyProveedores = [
    {
      id: 'prov1',
      nombre: 'Distribuidora San Martín',
      cuit: '30-12345678-9',
      localidad: 'Buenos Aires',
      saldo: -250000,
    },
    {
      id: 'prov2',
      nombre: 'Logística del Norte SA',
      cuit: '30-98765432-1',
      localidad: 'Salta',
      saldo: -50000,
    },
    {
      id: 'prov3',
      nombre: 'Transportes Rápidos',
      cuit: '30-55667788-2',
      localidad: 'Mendoza',
      saldo: -175000,
    },
  ];

  const shouldFetchClientes = mode === 'cliente' && isOpen;
  const [clientesResult] = useClientesCobranzas(
    { page: 1, pageSize: 20, filter: filtrosClientes },
    !shouldFetchClientes
  );

  const clientes = useMemo(() => {
    const edges = clientesResult.data?.clientes?.edges || [];
    return edges.map((edge) => ({
      id: edge.node.id,
      nombre: edge.node.razonSocial,
      cuit: edge.node.cuit,
      localidad: edge.node.localidad?.nombre || '',
      saldo: 0,
    }));
  }, [clientesResult.data]);

  const allData = mode === 'cliente' ? clientes : dummyProveedores;

  useEffect(() => {
    if (mode === 'cliente') {
      setFilteredData(allData);
      return;
    }

    if (search) {
      const filtered = allData.filter(
        (item) =>
          item.nombre.toLowerCase().includes(search.toLowerCase()) ||
          item.cuit.includes(search) ||
          item.localidad.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(allData);
    }
  }, [search, mode, allData]);

  const handleSelect = (item) => {
    onSelect(item);
    onClose();
  };

  const mostrarSaldo = mode !== 'cliente';
  const cargandoClientes = clientesResult.fetching && mode === 'cliente';

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="selector-title"
      className="modal modal-content w-full max-w-3xl focus-visible:outline-none p-0 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[rgb(var(--border))]">
        <h2
          id="selector-title"
          className="text-lg font-semibold text-[rgb(var(--fg))] flex items-center gap-2"
        >
          {mode === 'cliente' ? (
            <>
              <User className="w-5 h-5" aria-hidden="true" />
              Seleccionar Cliente
            </>
          ) : (
            <>
              <Building2 className="w-5 h-5" aria-hidden="true" />
              Seleccionar Proveedor
            </>
          )}
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-[rgba(var(--muted))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

        {/* Search */}
        <div className="p-4 border-b border-[rgb(var(--border))]">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]"
              aria-hidden="true"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, CUIT o localidad..."
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              aria-label="Buscar"
            />
          </div>
        </div>

        {/* Table */}
        <div className="max-h-96 overflow-y-auto">
          {cargandoClientes && (
            <p className="px-4 py-4 text-sm text-[rgb(var(--muted))]">
              Cargando clientes...
            </p>
          )}
          <table className="w-full">
            <thead className="sticky top-0 bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  CUIT
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                  Localidad
                </th>
                {mostrarSaldo && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                    Saldo
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--border))]">
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={`cursor-pointer hover:bg-[rgba(var(--muted),0.3)] transition-colors ${
                      selectedId === item.id ? 'bg-[rgba(var(--brand),0.1)]' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(item);
                      }
                    }}
                    aria-selected={selectedId === item.id}
                  >
                    <td className="px-4 py-3 text-sm text-[rgb(var(--fg))] font-medium">
                      {item.nombre}
                    </td>
                    <td className="px-4 py-3 text-sm text-[rgb(var(--fg))]">
                      {item.cuit}
                    </td>
                    <td className="px-4 py-3 text-sm text-[rgb(var(--muted))]">
                      {item.localidad}
                    </td>
                    {mostrarSaldo && (
                      <td
                        className={`px-4 py-3 text-sm text-right font-medium ${
                          item.saldo >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {new Intl.NumberFormat('es-AR', {
                          style: 'currency',
                          currency: 'ARS',
                          minimumFractionDigits: 0,
                        }).format(Math.abs(item.saldo))}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={mostrarSaldo ? 4 : 3} className="px-4 py-8 text-center text-[rgb(var(--muted))]">
                    No se encontraron resultados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[rgb(var(--border))] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        </div>
    </ModalBase>
  );
}
